import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem as MuiMenuItem,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  ListItemIcon,
  Divider,
} from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import ArchiveRounded from '@mui/icons-material/ArchiveRounded';
import RestoreRounded from '@mui/icons-material/RestoreRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import { useOrder } from '../../state/OrderContext';
import { useMenu } from '../../state/MenuContext';
import { useLayout } from '../../state/LayoutContext';
import { useAuth } from '../../state/AuthContext';
import { formatPrice } from '../../utils/format';
import { fetchRoster, postRosterAction } from '../../data/apiClient';
import type { MenuItem, MenuTab, TenantRoster } from '../../shared/types';

const COLOR_PALETTE = [
  '#F57F17',
  '#F9A825',
  '#3E2723',
  '#0277BD',
  '#4E342E',
  '#E64A19',
  '#B8860B',
  '#880E4F',
  '#C6A700',
  '#6A1B9A',
  '#BF360C',
  '#B71C1C',
  '#5D4037',
  '#E65100',
  '#6D4C41',
  '#78909C',
];

function newId(): string {
  return crypto.randomUUID();
}

interface ItemDraft {
  name: string;
  price: string;
  color: string;
  tabId: string;
}

interface AreaDraft {
  name: string;
  start: string;
  count: string;
}

const ColorPicker: React.FC<{ value: string; onChange: (color: string) => void }> = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
    {COLOR_PALETTE.map((color) => (
      <Box
        key={color}
        onClick={() => onChange(color)}
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          backgroundColor: color,
          cursor: 'pointer',
          boxShadow: value === color ? '0 0 0 3px #fff, 0 0 0 5px currentColor' : 'none',
          color: value === color ? 'primary.main' : 'transparent',
        }}
      />
    ))}
  </Box>
);

const AdminScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useAuth();
  const { state, toggleItemAvailability } = useOrder();
  const {
    tabs,
    addTab,
    renameTab,
    archiveTab,
    restoreTab,
    reorderTabs,
    addMenuItem,
    updateMenuItem,
    archiveMenuItem,
    restoreMenuItem,
    reorderItems,
  } = useMenu();
  const { areas, addArea, editArea, removeArea, reorderAreas } = useLayout();

  // Section navigation
  const [activeSection, setActiveSection] = useState(0);

  // Menu state
  const [newTabName, setNewTabName] = useState('');
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ItemDraft>({ name: '', price: '', color: COLOR_PALETTE[0], tabId: '' });
  const [renameTabDialogOpen, setRenameTabDialogOpen] = useState(false);
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingTabName, setEditingTabName] = useState('');

  // Overflow menu anchors
  const [itemMenuAnchor, setItemMenuAnchor] = useState<{ anchor: HTMLElement; itemId: string; tabId: string } | null>(null);
  const [tabMenuAnchor, setTabMenuAnchor] = useState<{ anchor: HTMLElement; tabId: string } | null>(null);
  const [areaMenuAnchor, setAreaMenuAnchor] = useState<{ anchor: HTMLElement; areaId: string } | null>(null);
  const [staffMenuAnchor, setStaffMenuAnchor] = useState<{ anchor: HTMLElement; email: string } | null>(null);

  // Areas state
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [editingAreaId, setEditingAreaId] = useState<string | null>(null);
  const [areaDialogDraft, setAreaDialogDraft] = useState<AreaDraft>({ name: '', start: '1', count: '10' });

  // Staff state
  const [roster, setRoster] = useState<TenantRoster | null>(null);
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [rosterSnackbar, setRosterSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'error',
  });

  useEffect(() => {
    if (!isAdmin) return;
    fetchRoster()
      .then(setRoster)
      .catch((e: Error) => setRosterSnackbar({ open: true, message: e.message, severity: 'error' }));
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, textAlign: 'center', color: '#9E9E9E' }}>
        <Typography>{t('admin.accessDenied')}</Typography>
      </Container>
    );
  }

  const activeTabs = tabs.filter((tab) => !tab.archived);
  const archivedTabs = tabs.filter((tab) => tab.archived);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const getTabIndex = (tabId: string) => activeTabs.findIndex((t) => t.id === tabId);

  const getActiveItemsInTab = (tabId: string): MenuItem[] => {
    const tab = activeTabs.find((t) => t.id === tabId);
    return tab ? tab.items.filter((i) => !i.archived) : [];
  };

  const getItemIndex = (tabId: string, itemId: string) =>
    getActiveItemsInTab(tabId).findIndex((i) => i.id === itemId);

  const isValidAreaDraft = (d: AreaDraft): boolean => {
    const start = parseInt(d.start, 10);
    const count = parseInt(d.count, 10);
    return d.name.trim().length > 0 && Number.isInteger(start) && start >= 1 && Number.isInteger(count) && count >= 1;
  };

  const priceValue = parseFloat(draft.price.replace(',', '.'));
  const itemDraftCanSubmit = draft.name.trim().length > 0 && !Number.isNaN(priceValue) && priceValue > 0;

  // ── Tab handlers ─────────────────────────────────────────────────────────

  const handleAddTab = (): void => {
    if (!newTabName.trim()) return;
    addTab(newId(), newTabName.trim());
    setNewTabName('');
  };

  const moveTab = (tabId: string, direction: -1 | 1): void => {
    const ids = activeTabs.map((t) => t.id);
    const idx = ids.indexOf(tabId);
    const swapWith = idx + direction;
    if (swapWith < 0 || swapWith >= ids.length) return;
    [ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]];
    reorderTabs(ids);
  };

  const submitRenameTab = (): void => {
    if (editingTabId && editingTabName.trim()) renameTab(editingTabId, editingTabName.trim());
    setRenameTabDialogOpen(false);
    setTabMenuAnchor(null);
  };

  // ── Item handlers ─────────────────────────────────────────────────────────

  const startAddItem = (tabId: string): void => {
    setEditingItemId(null);
    setDraft({ name: '', price: '', color: COLOR_PALETTE[0], tabId });
    setItemDialogOpen(true);
  };

  const startEditItem = (tab: MenuTab, item: MenuItem): void => {
    setEditingItemId(item.id);
    setDraft({ name: item.name, price: String(item.price), color: item.color, tabId: tab.id });
    setItemDialogOpen(true);
  };

  const closeItemDialog = (): void => {
    setItemDialogOpen(false);
    setEditingItemId(null);
  };

  const submitAddItem = (): void => {
    const price = parseFloat(draft.price.replace(',', '.'));
    addMenuItem(draft.tabId, newId(), draft.name.trim(), price, draft.color);
    closeItemDialog();
  };

  const submitEditItem = (): void => {
    if (!editingItemId) return;
    const price = parseFloat(draft.price.replace(',', '.'));
    updateMenuItem(editingItemId, { name: draft.name.trim(), price, color: draft.color, tabId: draft.tabId });
    closeItemDialog();
  };

  const moveItem = (tab: MenuTab, itemId: string, direction: -1 | 1): void => {
    const activeItems = tab.items.filter((i) => !i.archived);
    const ids = activeItems.map((i) => i.id);
    const idx = ids.indexOf(itemId);
    const swapWith = idx + direction;
    if (swapWith < 0 || swapWith >= ids.length) return;
    [ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]];
    reorderItems(tab.id, ids);
  };

  // ── Area handlers ─────────────────────────────────────────────────────────

  const startAddArea = (): void => {
    setEditingAreaId(null);
    setAreaDialogDraft({ name: '', start: '1', count: '10' });
    setAreaDialogOpen(true);
  };

  const startEditArea = (area: { id: string; name: string; start: number; count: number }): void => {
    setEditingAreaId(area.id);
    setAreaDialogDraft({ name: area.name, start: String(area.start), count: String(area.count) });
    setAreaDialogOpen(true);
  };

  const submitAreaDialog = (): void => {
    if (!isValidAreaDraft(areaDialogDraft)) return;
    const name = areaDialogDraft.name.trim();
    const start = parseInt(areaDialogDraft.start, 10);
    const count = parseInt(areaDialogDraft.count, 10);
    if (editingAreaId) {
      editArea(editingAreaId, name, start, count);
    } else {
      addArea(newId(), name, start, count);
    }
    setAreaDialogOpen(false);
    setAreaMenuAnchor(null);
  };

  const moveArea = (areaId: string, direction: -1 | 1): void => {
    const ids = areas.map((a) => a.id);
    const idx = ids.indexOf(areaId);
    const swapWith = idx + direction;
    if (swapWith < 0 || swapWith >= ids.length) return;
    [ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]];
    reorderAreas(ids);
  };

  // ── Staff handlers ────────────────────────────────────────────────────────

  const handleAddStaff = (): void => {
    const email = newStaffEmail.trim();
    if (!email) return;
    postRosterAction({ type: 'ADD_STAFF', email })
      .then((next) => { setRoster(next); setNewStaffEmail(''); })
      .catch((e: Error) => setRosterSnackbar({ open: true, message: t('admin.staffActionFailed', { message: e.message }), severity: 'error' }));
  };

  const handleRemoveStaff = (email: string): void => {
    postRosterAction({ type: 'REMOVE_STAFF', email })
      .then(setRoster)
      .catch((e: Error) => setRosterSnackbar({ open: true, message: t('admin.staffActionFailed', { message: e.message }), severity: 'error' }));
  };

  const handleSetAdmin = (email: string, isAdminNext: boolean): void => {
    postRosterAction({ type: 'SET_ADMIN', email, isAdmin: isAdminNext })
      .then(setRoster)
      .catch((e: Error) => setRosterSnackbar({ open: true, message: t('admin.staffActionFailed', { message: e.message }), severity: 'error' }));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="sm" sx={{ py: 2, pb: 4 }}>

      {/* Section navigation */}
      <Paper elevation={0} sx={{ borderBottom: '1px solid #F0F0F0', mb: 2.5 }}>
        <Tabs
          value={activeSection}
          onChange={(_, v) => { setActiveSection(v); window.scrollTo({ top: 0, behavior: 'instant' }); }}
          variant="fullWidth"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', minHeight: 44 } }}
        >
          <Tab label={t('admin.sectionMenu')} />
          <Tab label={t('admin.sectionAreas')} />
          <Tab label={t('admin.sectionStaff')} />
        </Tabs>
      </Paper>

      {/* ── Section 0: Speisekarte ── */}
      {activeSection === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, px: 0.5, mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('admin.addTabPlaceholder')}
              value={newTabName}
              onChange={(e) => setNewTabName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddTab(); }}
            />
            <Button variant="contained" startIcon={<AddRounded />} onClick={handleAddTab} disabled={!newTabName.trim()}>
              {t('admin.addTab')}
            </Button>
          </Box>

          {activeTabs.length === 0 && (
            <Typography sx={{ px: 0.5, color: '#9E9E9E', fontSize: '0.85rem', mb: 2 }}>{t('admin.noTabs')}</Typography>
          )}

          {activeTabs.map((tab, tabIdx) => {
            const activeItems = tab.items.filter((i) => !i.archived);
            const archivedItems = tab.items.filter((i) => i.archived);
            return (
              <Accordion
                key={tab.id}
                defaultExpanded={tabIdx === 0}
                disableGutters
                elevation={0}
                sx={{
                  mb: 1,
                  border: '1px solid #F0F0F0',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, mr: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#212121', flex: 1, minWidth: 0 }} noWrap>
                      {tab.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={activeItems.length}
                      sx={{ backgroundColor: '#F5F5F5', color: '#757575', fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setTabMenuAnchor({ anchor: e.currentTarget, tabId: tab.id }); }}
                    >
                      <MoreVertRounded fontSize="small" />
                    </IconButton>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <Paper elevation={0} sx={{ overflow: 'hidden', borderTop: '1px solid #F0F0F0', borderRadius: 0 }}>
                    {activeItems.length === 0 && (
                      <Typography sx={{ px: 1.75, py: 1.5, color: '#BDBDBD', fontSize: '0.8rem' }}>{t('admin.noItems')}</Typography>
                    )}
                    {activeItems.map((item) => {
                      const unavailable = !!state.unavailableItemIds[item.id];
                      return (
                        <Box key={item.id} sx={{ borderBottom: '1px solid #F9F9F9' }}>
                          <Box sx={{ px: 1.75, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: item.color, flexShrink: 0 }} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography noWrap sx={{ fontWeight: 500, fontSize: '0.9rem', color: unavailable ? '#BDBDBD' : '#212121' }}>
                                  {item.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#9E9E9E' }}>
                                  {formatPrice(item.price, i18n.language)}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                              <Chip
                                size="small"
                                label={unavailable ? t('admin.locked') : t('admin.available')}
                                onClick={() => toggleItemAvailability(item.id)}
                                sx={{
                                  backgroundColor: unavailable ? '#FFCDD2' : '#E8F5E9',
                                  color: unavailable ? 'primary.main' : '#2E7D32',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  cursor: 'pointer',
                                }}
                              />
                              <IconButton size="small" aria-label={t('admin.edit')} onClick={() => startEditItem(tab, item)}>
                                <EditRounded fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => setItemMenuAnchor({ anchor: e.currentTarget, itemId: item.id, tabId: tab.id })}
                              >
                                <MoreVertRounded fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Paper>

                  <Box sx={{ px: 1.25, py: 1 }}>
                    <Button size="small" startIcon={<AddRounded />} onClick={() => startAddItem(tab.id)}>
                      {t('admin.addItem')}
                    </Button>
                  </Box>

                  {archivedItems.length > 0 && (
                    <Box sx={{ px: 1.25, pb: 1.25, borderTop: '1px solid #F9F9F9' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#BDBDBD', mt: 1, mb: 0.5 }}>{t('admin.archivedItems')}</Typography>
                      {archivedItems.map((item) => (
                        <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography sx={{ fontSize: '0.8rem', color: '#9E9E9E' }}>{item.name}</Typography>
                          <Button size="small" startIcon={<RestoreRounded />} onClick={() => restoreMenuItem(item.id)}>
                            {t('admin.restore')}
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {archivedTabs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#BDBDBD', px: 0.5, mb: 0.5 }}>{t('admin.archivedTabs')}</Typography>
              {archivedTabs.map((tab) => (
                <Box key={tab.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, py: 0.5 }}>
                  <Typography sx={{ fontSize: '0.85rem', color: '#9E9E9E' }}>{tab.name}</Typography>
                  <Button size="small" startIcon={<RestoreRounded />} onClick={() => restoreTab(tab.id)}>
                    {t('admin.restore')}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ── Section 1: Tische & Bereiche ── */}
      {activeSection === 1 && (
        <Box>
          {areas.length === 0 ? (
            <Typography sx={{ px: 0.5, color: '#9E9E9E', fontSize: '0.85rem', mb: 2 }}>
              {t('admin.areas.noAreas')}
            </Typography>
          ) : (
            <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid #F5F5F5', mb: 2 }}>
              {areas.map((area) => {
                const rangeEnd = area.start + area.count - 1;
                return (
                  <Box key={area.id} sx={{ borderBottom: '1px solid #F9F9F9' }}>
                    <Box sx={{ px: 1.75, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#212121' }}>{area.name}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#9E9E9E' }}>
                          {`Tisch ${area.start}–${rangeEnd} · ${area.count} Tische`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                        <IconButton size="small" aria-label={t('admin.edit')} onClick={() => startEditArea(area)}>
                          <EditRounded fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => setAreaMenuAnchor({ anchor: e.currentTarget, areaId: area.id })}
                        >
                          <MoreVertRounded fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Paper>
          )}
          <Box sx={{ px: 0.5 }}>
            <Button startIcon={<AddRounded />} onClick={startAddArea}>
              {t('admin.areas.add')}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Section 2: Personal ── */}
      {activeSection === 2 && (
        <Box>
          <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #F5F5F5', borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={t('admin.staffAddPlaceholder')}
                value={newStaffEmail}
                onChange={(e) => setNewStaffEmail(e.target.value)}
                inputProps={{ inputMode: 'email', autoCapitalize: 'none', autoCorrect: 'off' }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddStaff(); }}
              />
              <Button variant="contained" startIcon={<AddRounded />} onClick={handleAddStaff} disabled={!newStaffEmail.trim()}>
                {t('admin.staffAdd')}
              </Button>
            </Box>
          </Paper>

          {roster && roster.allowedEmails.length === 0 && (
            <Typography sx={{ px: 0.5, color: '#9E9E9E', fontSize: '0.85rem' }}>{t('admin.staffNoStaff')}</Typography>
          )}

          {roster && roster.allowedEmails.length > 0 && (
            <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid #F5F5F5' }}>
              {roster.allowedEmails.map((email) => {
                const isEmailAdmin = roster.adminEmails.includes(email);
                return (
                  <Box
                    key={email}
                    sx={{ px: 1.75, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: '1px solid #F9F9F9' }}
                  >
                    <Typography noWrap sx={{ fontSize: '0.9rem', color: '#212121', flex: 1, minWidth: 0 }}>
                      {email}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Chip
                        size="small"
                        label={isEmailAdmin ? t('admin.staffAdminBadge') : t('admin.staffStaffBadge')}
                        onClick={() => handleSetAdmin(email, !isEmailAdmin)}
                        sx={{
                          backgroundColor: isEmailAdmin ? '#E8F5E9' : '#FFECEC',
                          color: isEmailAdmin ? '#2E7D32' : 'primary.main',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          cursor: 'pointer',
                        }}
                      />
                      <IconButton size="small" onClick={(e) => setStaffMenuAnchor({ anchor: e.currentTarget, email })}>
                        <MoreVertRounded fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Paper>
          )}
        </Box>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* Overflow Menus                                */}
      {/* ══════════════════════════════════════════════ */}

      {/* Tab overflow menu */}
      <Menu
        anchorEl={tabMenuAnchor?.anchor}
        open={Boolean(tabMenuAnchor)}
        onClose={() => setTabMenuAnchor(null)}
      >
        <MuiMenuItem
          onClick={() => {
            const name = activeTabs.find((t) => t.id === tabMenuAnchor?.tabId)?.name ?? '';
            setEditingTabId(tabMenuAnchor?.tabId ?? null);
            setEditingTabName(name);
            setRenameTabDialogOpen(true);
            setTabMenuAnchor(null);
          }}
        >
          <ListItemIcon><EditRounded fontSize="small" /></ListItemIcon>
          {t('admin.rename')}
        </MuiMenuItem>
        <Divider />
        <MuiMenuItem
          disabled={getTabIndex(tabMenuAnchor?.tabId ?? '') <= 0}
          onClick={() => { if (tabMenuAnchor) { moveTab(tabMenuAnchor.tabId, -1); setTabMenuAnchor(null); } }}
        >
          <ListItemIcon><ArrowUpwardRounded fontSize="small" /></ListItemIcon>
          {t('admin.moveUp')}
        </MuiMenuItem>
        <MuiMenuItem
          disabled={getTabIndex(tabMenuAnchor?.tabId ?? '') >= activeTabs.length - 1}
          onClick={() => { if (tabMenuAnchor) { moveTab(tabMenuAnchor.tabId, 1); setTabMenuAnchor(null); } }}
        >
          <ListItemIcon><ArrowDownwardRounded fontSize="small" /></ListItemIcon>
          {t('admin.moveDown')}
        </MuiMenuItem>
        <Divider />
        <MuiMenuItem
          onClick={() => { if (tabMenuAnchor) { archiveTab(tabMenuAnchor.tabId); setTabMenuAnchor(null); } }}
          sx={{ color: '#9E9E9E' }}
        >
          <ListItemIcon><ArchiveRounded fontSize="small" sx={{ color: '#9E9E9E' }} /></ListItemIcon>
          {t('admin.archive')}
        </MuiMenuItem>
      </Menu>

      {/* Item overflow menu */}
      <Menu
        anchorEl={itemMenuAnchor?.anchor}
        open={Boolean(itemMenuAnchor)}
        onClose={() => setItemMenuAnchor(null)}
      >
        <MuiMenuItem
          disabled={getItemIndex(itemMenuAnchor?.tabId ?? '', itemMenuAnchor?.itemId ?? '') <= 0}
          onClick={() => {
            if (itemMenuAnchor) {
              const tab = activeTabs.find((t) => t.id === itemMenuAnchor.tabId);
              if (tab) moveItem(tab, itemMenuAnchor.itemId, -1);
              setItemMenuAnchor(null);
            }
          }}
        >
          <ListItemIcon><ArrowUpwardRounded fontSize="small" /></ListItemIcon>
          {t('admin.moveUp')}
        </MuiMenuItem>
        <MuiMenuItem
          disabled={getItemIndex(itemMenuAnchor?.tabId ?? '', itemMenuAnchor?.itemId ?? '') >= getActiveItemsInTab(itemMenuAnchor?.tabId ?? '').length - 1}
          onClick={() => {
            if (itemMenuAnchor) {
              const tab = activeTabs.find((t) => t.id === itemMenuAnchor.tabId);
              if (tab) moveItem(tab, itemMenuAnchor.itemId, 1);
              setItemMenuAnchor(null);
            }
          }}
        >
          <ListItemIcon><ArrowDownwardRounded fontSize="small" /></ListItemIcon>
          {t('admin.moveDown')}
        </MuiMenuItem>
        <Divider />
        <MuiMenuItem
          onClick={() => { if (itemMenuAnchor) { archiveMenuItem(itemMenuAnchor.itemId); setItemMenuAnchor(null); } }}
          sx={{ color: '#9E9E9E' }}
        >
          <ListItemIcon><ArchiveRounded fontSize="small" sx={{ color: '#9E9E9E' }} /></ListItemIcon>
          {t('admin.archive')}
        </MuiMenuItem>
      </Menu>

      {/* Area overflow menu */}
      <Menu
        anchorEl={areaMenuAnchor?.anchor}
        open={Boolean(areaMenuAnchor)}
        onClose={() => setAreaMenuAnchor(null)}
      >
        <MuiMenuItem
          disabled={areas.findIndex((a) => a.id === areaMenuAnchor?.areaId) <= 0}
          onClick={() => { if (areaMenuAnchor) { moveArea(areaMenuAnchor.areaId, -1); setAreaMenuAnchor(null); } }}
        >
          <ListItemIcon><ArrowUpwardRounded fontSize="small" /></ListItemIcon>
          {t('admin.moveUp')}
        </MuiMenuItem>
        <MuiMenuItem
          disabled={areas.findIndex((a) => a.id === areaMenuAnchor?.areaId) >= areas.length - 1}
          onClick={() => { if (areaMenuAnchor) { moveArea(areaMenuAnchor.areaId, 1); setAreaMenuAnchor(null); } }}
        >
          <ListItemIcon><ArrowDownwardRounded fontSize="small" /></ListItemIcon>
          {t('admin.moveDown')}
        </MuiMenuItem>
        <Divider />
        <MuiMenuItem
          onClick={() => { if (areaMenuAnchor) { removeArea(areaMenuAnchor.areaId); setAreaMenuAnchor(null); } }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteRounded fontSize="small" color="error" /></ListItemIcon>
          {t('admin.areas.delete')}
        </MuiMenuItem>
      </Menu>

      {/* Staff overflow menu */}
      <Menu
        anchorEl={staffMenuAnchor?.anchor}
        open={Boolean(staffMenuAnchor)}
        onClose={() => setStaffMenuAnchor(null)}
      >
        <MuiMenuItem
          onClick={() => { if (staffMenuAnchor) { handleRemoveStaff(staffMenuAnchor.email); setStaffMenuAnchor(null); } }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteRounded fontSize="small" color="error" /></ListItemIcon>
          {t('admin.staffRemove')}
        </MuiMenuItem>
      </Menu>

      {/* ══════════════════════════════════════════════ */}
      {/* Dialogs                                       */}
      {/* ══════════════════════════════════════════════ */}

      {/* Item form dialog */}
      <Dialog open={itemDialogOpen} onClose={closeItemDialog} fullWidth maxWidth="xs" scroll="paper">
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {editingItemId ? t('admin.editItemTitle') : t('admin.addItemTitle')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '8px !important' }}>
          <TextField
            size="small"
            label={t('admin.itemNamePlaceholder')}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            autoFocus
          />
          <TextField
            size="small"
            label={t('admin.itemPricePlaceholder')}
            value={draft.price}
            onChange={(e) => setDraft({ ...draft, price: e.target.value })}
            inputProps={{ inputMode: 'decimal' }}
          />
          {editingItemId && (
            <TextField
              select
              size="small"
              label={t('admin.moveToTab')}
              value={draft.tabId}
              onChange={(e) => setDraft({ ...draft, tabId: e.target.value })}
            >
              {activeTabs.map((tab) => (
                <MuiMenuItem key={tab.id} value={tab.id}>{tab.name}</MuiMenuItem>
              ))}
            </TextField>
          )}
          <Typography sx={{ fontSize: '0.75rem', color: '#9E9E9E' }}>{t('admin.itemColor')}</Typography>
          <ColorPicker value={draft.color} onChange={(color) => setDraft({ ...draft, color })} />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={closeItemDialog}>{t('admin.cancel')}</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            disabled={!itemDraftCanSubmit}
            onClick={editingItemId ? submitEditItem : submitAddItem}
          >
            {t('admin.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename tab dialog */}
      <Dialog open={renameTabDialogOpen} onClose={() => setRenameTabDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{t('admin.renameTabTitle')}</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField
            size="small"
            fullWidth
            value={editingTabName}
            onChange={(e) => setEditingTabName(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') submitRenameTab(); }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setRenameTabDialogOpen(false)}>{t('admin.cancel')}</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" disabled={!editingTabName.trim()} onClick={submitRenameTab}>
            {t('admin.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Area dialog */}
      <Dialog open={areaDialogOpen} onClose={() => setAreaDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {editingAreaId ? t('admin.areas.editTitle') : t('admin.areas.addTitle')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '8px !important' }}>
          <TextField
            size="small"
            label={t('admin.areas.namePlaceholder')}
            value={areaDialogDraft.name}
            onChange={(e) => setAreaDialogDraft({ ...areaDialogDraft, name: e.target.value })}
            autoFocus
          />
          <TextField
            size="small"
            label={t('admin.areas.startPlaceholder')}
            value={areaDialogDraft.start}
            onChange={(e) => setAreaDialogDraft({ ...areaDialogDraft, start: e.target.value })}
            inputProps={{ inputMode: 'numeric', min: 1 }}
          />
          <TextField
            size="small"
            label={t('admin.areas.countPlaceholder')}
            value={areaDialogDraft.count}
            onChange={(e) => setAreaDialogDraft({ ...areaDialogDraft, count: e.target.value })}
            inputProps={{ inputMode: 'numeric', min: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setAreaDialogOpen(false)}>{t('admin.cancel')}</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" disabled={!isValidAreaDraft(areaDialogDraft)} onClick={submitAreaDialog}>
            {t('admin.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staff error / success snackbar */}
      <Snackbar
        open={rosterSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setRosterSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={rosterSnackbar.severity} onClose={() => setRosterSnackbar((s) => ({ ...s, open: false }))}>
          {rosterSnackbar.message}
        </Alert>
      </Snackbar>

    </Container>
  );
};

export default AdminScreen;
